import React, { useEffect } from "react";
import { useSearchParams } from "react-router-dom";

import { useAppDispatch, useAppSelector } from "store";
import { getPlayerRiskCalculated } from "store/players/list/slice";

import ModalTabSkeleton from "components/global/Skeleton/ModalTabSkeleton/ModalTabSkeleton";
import EmptyState from "components/EmptyState/EmptyState";
import { useTranslation } from "react-i18next";

const restrictionLabels: Record<string, string> = {
  user: "User",
  deposit: "Deposit",
  withdraw: "Withdraw",
  casino: "Casino",
  sport: "Sport",
  poker: "Poker",
  bonus: "Bonus",
  cashback: "Cashback",
  promoCode: "Promo code",
  tournament: "Tournament",
  transfer: "Transfer",
  cryptoConvert: "Crypto convert",
  affiliate: "Affiliate",
};

const restrictionOrder = [
  "user",
  "deposit",
  "withdraw",
  "casino",
  "sport",
  "poker",
  "bonus",
  "cashback",
  "promoCode",
  "tournament",
  "transfer",
  "cryptoConvert",
  "affiliate",
];

const restrictionIcons: Record<string, string> = {
  deposit: "/sidebar/deposits.svg",
  withdraw: "/sidebar/withdrawals.svg",
  casino: "/sidebar/casino.svg",
  sport: "/sidebar/sport.svg",
  poker: "/sidebar/poker.svg",
  bonus: "/sidebar/bonuses.svg",
  cashback: "/sidebar/cashbacks.svg",
  promoCode: "/sidebar/promocodes.svg",
  tournament: "/sidebar/tournaments.svg",
  transfer: "/sidebar/transfer.svg",
  cryptoConvert: "/sidebar/currency.svg",
  affiliate: "/sidebar/affiliates.svg",
  user: "/sidebar/player.svg",
};

const ListCalculatedTab: React.FC = () => {
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  const entityId = searchParams.get("entityId");
  const playerCurrency = useAppSelector(
    (store) => store.players.payload?.currency,
  );
  const calculatedRestrictions = useAppSelector(
    (store) => store.players.calculatedRestrictions,
  );
  const isCalculatedFetched = useAppSelector(
    (store) => store.players.isCalculatedFetched,
  );
  const { t } = useTranslation();

  useEffect(() => {
    if (entityId) {
      dispatch(
        getPlayerRiskCalculated({
          id: entityId,
          currency: playerCurrency,
        }),
      );
    }
  }, [dispatch, entityId, playerCurrency]);

  if (!isCalculatedFetched) {
    return <ModalTabSkeleton />;
  }

  const sortedItems =
    calculatedRestrictions?.items
      ?.slice()
      ?.sort((a, b) => {
        return (
          restrictionOrder.indexOf(a.restrictionType) -
          restrictionOrder.indexOf(b.restrictionType)
        );
      })
      ?.map((item, index) => ({
        id: index + 1,
        restrictionType: item.restrictionType,
        restriction:
          restrictionLabels[item.restrictionType] ?? item.restrictionType,

        status: item.allowed ? "Allowed" : "Blocked",

        limit: item.limit
          ? `${(
              item.limit.amountMinor /
              10 ** item.limit.currencyScale
            ).toLocaleString(undefined, {
              minimumFractionDigits: item.limit.currencyScale,
              maximumFractionDigits: item.limit.currencyScale,
            })} ${item.limit.currency}`
          : "—",

        source: item.source ?? "—",

        reason: item.reason ?? "—",
      })) ?? [];

  return (
    <div className="risk-calculated-content">
      {sortedItems.length === 0 && (
        <EmptyState message={t("noCalculatedRestrictionsFound")} />
      )}

      {sortedItems.length > 0 && (
        <div className="inner-table-wrapper risk-calculated-table">
          <table>
            <thead>
              <tr>
                <th>Restriction</th>
                <th>Status</th>
                <th>Limit</th>
                <th>Reason</th>
                <th>{t("source")}</th>
              </tr>
            </thead>

            <tbody>
              {sortedItems.map((row) => (
                <tr key={row.id}>
                  <td>
                    <div className="restriction-cell">
                      <img
                        src={restrictionIcons[row.restrictionType]}
                        alt={row.restriction}
                      />
                      <span>{row.restriction}</span>
                    </div>
                  </td>

                  <td>
                    <span
                      className={`badge ${
                        row.status === "Allowed"
                          ? "badge--low"
                          : "badge--blocked"
                      }`}
                    >
                      {row.status}
                    </span>
                  </td>

                  <td>{row.limit}</td>
                  <td>{row.reason}</td>
                  <td>{row.source}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ListCalculatedTab;
